// Trip categories, as the app's TripCategory and TripCategoryCatalog define them. Trips and rates
// store a raw value: a built-in's name, or a custom category's UUID string.

export type CategoryColor =
  | "pine" | "slate" | "ink" | "brick" | "amber" | "plum"
  | "rose" | "forest" | "sky" | "cocoa" | "graphite";

/** Badge and button fills: deep enough for white glyphs at 4.5:1 in light and dark (Theme.swift). */
export const colorTint: Record<CategoryColor, string> = {
  pine: "#1F5F5B",
  slate: "#5D677D",
  ink: "#2F4468",
  brick: "#C2462A",
  amber: "#9C5D0A",
  plum: "#7B3F9E",
  rose: "#B0305E",
  forest: "#2F6B34",
  sky: "#1D6A99",
  cocoa: "#7A5236",
  graphite: "#4A4F57",
};

/** Chart bars and proportion strips: brighter steps of the same families (Theme.swift). */
export const colorChartTint: Record<CategoryColor, string> = {
  pine: "#009683",
  slate: "#A869C9",
  ink: "#3F72DB",
  brick: "#D95A2B",
  amber: "#B8870A",
  plum: "#9B59C9",
  rose: "#E0578A",
  forest: "#46923F",
  sky: "#288AC4",
  cocoa: "#A9774F",
  graphite: "#7E848E",
};

export interface Category {
  rawValue: string;
  name: string;
  /** The app's SF Symbol name; the web draws its own icon for it. */
  symbol: string;
  color: CategoryColor;
  /** Asks for a purpose, and is in mileage logs by default. */
  isDeductible: boolean;
  isCustom: boolean;
  isArchived: boolean;
  /** Position in lists and pickers. */
  listRank: number;
  /** Position from the bottom of a stacked chart. */
  chartRank: number;
}

function builtIn(rawValue: string, name: string, symbol: string, color: CategoryColor, isDeductible: boolean, listRank: number, chartRank: number): Category {
  return { rawValue, name, symbol, color, isDeductible, isCustom: false, isArchived: false, listRank, chartRank };
}

export const builtIns: readonly Category[] = [
  builtIn("business", "Business", "briefcase.fill", "pine", true, 0, 0),
  builtIn("personal", "Personal", "house.fill", "slate", false, 1, 2),
  builtIn("commute", "Commute", "building.2.fill", "ink", false, 2, 4),
  builtIn("medical", "Medical", "cross.case.fill", "brick", true, 3, 1),
  builtIn("charity", "Charity", "heart.fill", "amber", true, 4, 3),
];

/** A custom category as the server stores it. */
export interface CustomCategoryInput {
  id: string;
  name: string;
  systemImage: string;
  color: string;
  isDeductible: boolean;
  isArchived: boolean;
  sortOrder: number;
  createdAt: Date;
}

const colorNames = new Set<string>(Object.keys(colorTint));

/** Every category this account knows: the built-ins, then custom ones in the user's order. */
export class CategoryCatalog {
  readonly all: readonly Category[];
  private readonly byRaw: Map<string, Category>;

  constructor(custom: readonly CustomCategoryInput[]) {
    const seen = new Set<string>();
    const ordered = [...custom]
      .sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.getTime() - b.createdAt.getTime())
      // Sync can leave two with the same id; the first wins, as in the app.
      .filter((category) => {
        const key = category.id.toUpperCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    const customs = ordered.map((category, position): Category => ({
      rawValue: category.id.toUpperCase(),
      name: category.name,
      symbol: category.systemImage,
      color: colorNames.has(category.color) ? (category.color as CategoryColor) : "plum",
      isDeductible: category.isDeductible,
      isCustom: true,
      isArchived: category.isArchived,
      listRank: builtIns.length + position,
      chartRank: builtIns.length + position,
    }));
    this.all = [...builtIns, ...customs];
    this.byRaw = new Map(this.all.map((category) => [category.rawValue, category]));
  }

  /**
   * The category for a trip's or rate's raw value. A custom category the server doesn't have,
   * such as one deleted elsewhere, reads as undefined: unclassified, as in the app.
   */
  resolve(raw: string | null | undefined): Category | undefined {
    if (!raw) return undefined;
    return this.byRaw.get(raw) ?? this.byRaw.get(raw.toUpperCase());
  }

  /** What the pickers offer: everything but archived custom categories, plus `including`. */
  choices(including?: Category): Category[] {
    const choices = this.all.filter((category) => !category.isArchived);
    if (including && !choices.some((category) => category.rawValue === including.rawValue)) {
      choices.push(including);
    }
    return choices;
  }

  /** Categories in mileage logs by default. */
  get deductible(): Category[] {
    return this.choices().filter((category) => category.isDeductible);
  }

  /** In stacked-chart order, bottom first. */
  get chartOrder(): Category[] {
    return [...this.all].sort((a, b) => a.chartRank - b.chartRank);
  }
}
